/**
 * Renders the cited subgraph of an Ask answer as a small knowledge graph —
 * nodes (colored by type) joined by typed relationship edges — so the user
 * SEES how the answer's settings connect, not just a flat list.
 *
 * Pure inline SVG with a deterministic radial layout (no external lib), so
 * it renders identically in the app, a standalone export, and a PDF, and
 * adapts to light/dark via the node-type palette below.
 */

import { AskGraph } from './hooks';

interface Props {
  graph: AskGraph;
  citedIds?: string[];
}

// Categorical palette keyed by node type. Mid-tone hues chosen to stay
// legible on both light and dark backgrounds; anything unmapped falls back
// to slate. This is the ONE place color encodes node type.
const TYPE_COLOR: Record<string, string> = {
  PIPELINE: '#4453D6',
  PIPELINE_STAGE: '#0E9488',
  TICKET_TYPE: '#B97910',
  TICKET_CATEGORY: '#C2410C',
  SLA_POLICY: '#D0433A',
  AUTOMATION_RULE: '#7A3FC4',
  FIELD_DEFINITION: '#2563EB',
  N8N_WORKFLOW: '#0891B2',
  KB_ARTICLE: '#15803D',
};
const FALLBACK = '#64748B';

function colorFor(type: string): string {
  return TYPE_COLOR[type] ?? FALLBACK;
}

function truncate(s: string, n = 20): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export default function OrgBrainGraph({ graph, citedIds = [] }: Props) {
  const nodes = graph?.nodes ?? [];
  const edges = graph?.edges ?? [];
  if (nodes.length === 0) return null;

  const W = 640;
  const H = Math.max(260, Math.min(420, 160 + nodes.length * 26));
  const cx = W / 2;
  const cy = H / 2;
  const R = Math.min(W, H) / 2 - 70;
  const cited = new Set(citedIds);

  // Deterministic layout: one node centered, the rest evenly on a ring.
  const pos = new Map<string, { x: number; y: number }>();
  if (nodes.length === 1) {
    pos.set(nodes[0].id, { x: cx, y: cy });
  } else {
    nodes.forEach((n, i) => {
      const angle = (i / nodes.length) * Math.PI * 2 - Math.PI / 2;
      pos.set(n.id, { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) });
    });
  }

  const drawableEdges = edges.filter(
    (e) => pos.has(e.from_node_id) && pos.has(e.to_node_id),
  );

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`Knowledge graph of ${nodes.length} settings and ${drawableEdges.length} relationships`}
        style={{ maxWidth: '100%', display: 'block' }}
      >
        {/* Edges (drawn first, under nodes) */}
        {drawableEdges.map((e, i) => {
          const a = pos.get(e.from_node_id)!;
          const b = pos.get(e.to_node_id)!;
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          return (
            <g key={`e-${i}`}>
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="currentColor"
                strokeOpacity={0.25}
                strokeWidth={1.5}
              />
              <text
                x={mx}
                y={my}
                fontSize="9"
                textAnchor="middle"
                fill="currentColor"
                fillOpacity={0.55}
                style={{ fontFamily: 'ui-monospace, monospace' }}
              >
                {e.type.toLowerCase().replace(/_/g, ' ')}
              </text>
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((n) => {
          const p = pos.get(n.id)!;
          const isCited = cited.has(n.id);
          const c = colorFor(n.type);
          return (
            <g key={n.id}>
              {isCited && (
                <circle cx={p.x} cy={p.y} r={12} fill="none" stroke={c} strokeOpacity={0.4} strokeWidth={2} />
              )}
              <circle cx={p.x} cy={p.y} r={isCited ? 8 : 6} fill={c} />
              <text
                x={p.x}
                y={p.y + 22}
                fontSize="11"
                textAnchor="middle"
                fill="currentColor"
                style={{ fontWeight: isCited ? 600 : 400 }}
              >
                {truncate(n.natural_key)}
              </text>
              <text
                x={p.x}
                y={p.y + 35}
                fontSize="8.5"
                textAnchor="middle"
                fill={c}
                style={{ fontFamily: 'ui-monospace, monospace' }}
              >
                {n.type.toLowerCase()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}